import { useQuery } from '@tanstack/vue-query'
import type { Ref } from 'vue'
import { computed } from 'vue'

import { getCycles, V1_CYCLES_QUERY_KEY } from '@/features/admin/reminders/api'
import type { GetCyclesResponseType } from '@/features/admin/reminders/types'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { MAX_CYCLES_PER_PAGE } from '@/utils/config/constants'

function V1ToV2DataMiddleware({ data }: { data: GetCyclesResponseType | undefined }) {
  const cycles = data?.cycles?.map((cycle) => {
    return {
      id: cycle.id,
      name: cycle.name,
      startDate: cycle.startDate,
      endDate: cycle.endDate,
      programIds: cycle.programIds,
      createdAt: cycle.createdAt,
      updatedAt: cycle.updatedAt,
      reminders: cycle.reminders,
      programs: cycle.programs,
    }
  })

  return cycles || []
}

const useCycles = ({
  pageRef,
  perPageRef,
}: {
  pageRef?: Ref<number>
  perPageRef?: Ref<number>
} = {}) => {
  const { accountRef } = useAccount()

  const getCyclesPageRef = computed(() => pageRef?.value || 1)
  const getCyclesPerPageRef = computed(() => perPageRef?.value || 20)

  // Vérifier si toutes les données nécessaires sont présentes pour effectuer la requête
  const isCyclesQueryEnabledRef = computed(() => !!accountRef.value)

  const cyclesQuery = useQuery({
    queryKey: [
      V1_CYCLES_QUERY_KEY,
      {
        page: getCyclesPageRef,
        perPage: getCyclesPerPageRef,
      },
    ],
    queryFn: () =>
      getCycles({
        page: getCyclesPageRef.value!,
        perPage: getCyclesPerPageRef.value ?? MAX_CYCLES_PER_PAGE,
      }),
    enabled: isCyclesQueryEnabledRef,
  })

  const data = computed(() => {
    return V1ToV2DataMiddleware({
      data: cyclesQuery.data.value,
    })
  })

  const totalCyclesPagesRef = computed(() => cyclesQuery.data.value?.totalPages)
  const totalCyclesRef = computed(() => cyclesQuery.data.value?.totalCycles)

  return {
    cyclesRef: data,
    totalCyclesPagesRef,
    totalCyclesRef,
    isPendingCyclesRef: cyclesQuery.isPending,
  }
}

export default useCycles
